import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, filter } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
	PRODUCT_FIND,
	PRODUCT_FIND_SUCCESS,
	PRODUCT_FAILED,
	PRODUCT_UPDATE_LOADING,
	PRODUCT_UPDATE_STOCK,
	PRODUCT_ALL_UPDATE_STOCK,
	SUGGESTION_ITEMS_FIND,
	SUGGESTION_ITEMS_FIND_SUCCESS,
} from '../actions';
import { MenuItemService } from '@core/services/menu-item/menu-item.service';
import {
  selectAvailabilityList,
	selectLocalChannelId,
	selectLocalChannelSesion,
	selectMenu,
	selectWorkDay,
} from '../selectors';
import { MenuItem } from '@core/models';
import { shouldUpdateAllStock } from '@core/domain/local-channel';
import { menuItemProcessor } from '@/core/domain/menu';
import { IntegerPrecisionService } from '@/core/services';
import { CartService } from '@/core/services';

@Injectable()
export class ProductEffects {
	constructor(
		private actions$: Actions,
		private menuItemService: MenuItemService,
		private store: Store<Store>,
    	private integerPrecisionService: IntegerPrecisionService,
		private cartService: CartService
	) {}

	find$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PRODUCT_FIND),
			withLatestFrom(
				this.store.select(selectLocalChannelId),
				this.store.select(selectMenu),
				this.store.select(selectWorkDay),
        this.store.select(selectAvailabilityList),
			),
			filter(([_, localChannelId, menu, workDay]) => !!localChannelId && !!menu && !!workDay),
			map(([action, localChannelId, menu, workDay, availabilityList]) => {
				return {
					suggestionItems: action?.suggestionItems ?? null,
					productName: action?.productName ?? null,
					localChannelId,
					menuId: menu?.idMenu ?? null,
					workDay,
					subcategoryId: action.subcategoryId,
          availabilityList
				};
			}),
			mergeMap(({ suggestionItems, productName, localChannelId, menuId, subcategoryId, availabilityList }) => {
				this.store.dispatch(PRODUCT_UPDATE_LOADING({ loading: true }));
				return this.menuItemService
					.getMenuItemList(suggestionItems, productName, subcategoryId, localChannelId, menuId)
					.pipe(
            map(response => {
							return {
								...response,
								data: menuItemProcessor.processCatalog(
									response?.data ?? [],
									availabilityList ?? [],
									this.integerPrecisionService,
									new Date()
								),
							};
						}),
						mergeMap(({ data }) => {
							const productList = data as MenuItem[];

							return [
								PRODUCT_FIND_SUCCESS({ productList }),
								PRODUCT_UPDATE_LOADING({ loading: false }),

                // TO DO - CHEQUEAR SI ES NECESARIO
								// CART_UPDATE_STOCK_ALL(),
							];
						}),
						catchError(error => of(PRODUCT_FAILED({ error })))
					);
			})
		)
	);

	updateStock$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PRODUCT_UPDATE_STOCK),
			filter(({ id, cantidad, operation }) => !!id && !!cantidad && !!operation),

			mergeMap(({ id, cantidad, operation }) => {
				return this.menuItemService.updateStock(id, cantidad, operation).pipe(
					mergeMap(({ data }) => {
						const [item] = data;
						return [
							// CART_UPDATE_STATE({item}),
						];
					}),
					catchError(error => of(PRODUCT_FAILED({ error })))
				);
			})
		)
	);

	updateAllStock$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PRODUCT_ALL_UPDATE_STOCK),
			withLatestFrom(
				this.cartService.getStoreForStore(),
				this.store.select(selectLocalChannelSesion)
			),
			filter(([_, __, localChannelSesion]) => shouldUpdateAllStock(localChannelSesion)),
			map(([_, cartItemList]) => {
				return {
					cartItemList: cartItemList as MenuItem[],
				};
			}),
			mergeMap(({ cartItemList }) => {
				return this.menuItemService.updateAllStock(cartItemList).pipe(
					mergeMap(() => {
						return [
							// CART_UPDATE_STATE({item}),
						];
					}),
					catchError(error => of(PRODUCT_FAILED({ error })))
				);
			})
		)
	);

	loadSuggestionItems$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SUGGESTION_ITEMS_FIND),
			withLatestFrom(this.store.select(selectLocalChannelId), this.store.select(selectWorkDay)),
			switchMap(([action, localChannelId]) =>
				this.menuItemService.getMenuItemList(action.ids, null, null, localChannelId).pipe(
					map(({ data }) =>
						SUGGESTION_ITEMS_FIND_SUCCESS({
							suggestionItems: data,
						})
					),
					catchError(error => of(PRODUCT_FAILED({ error })))
				)
			)
		);
	});
}
